import { DynamoDBClient, ScanCommand, UpdateItemCommand, DeleteItemCommand, PutItemCommand, GetItemCommand } from "@aws-sdk/client-dynamodb";

export interface Ticket {
	id: string; // ticketId in DynamoDB
	userId: string;
	email: string; // userEmail
	name: string; // userName
	category: string; // 'bug' | 'video' | 'quiz' | 'content' | 'other'
	subject: string; // title
	description: string;
	deviceOs?: string;
	deviceVersion?: string;
	appVersion?: string;
	status: 'Pending' | 'In Progress' | 'Resolved';
	createdAt: string;
	updatedAt?: string;
	reply?: string; // adminReply
	resolvedAt?: string;
	resolvedBy?: string;
	org?: string;
	attachment?: string;
}

const TABLE_NAME = 'xnav-v5-dev-support-tickets';
const REGION = 'us-west-2';

/**
 * Fetch all tickets from AWS DynamoDB table xnav-v5-dev-support-tickets
 */
export async function fetchTicketsFromDynamo(client?: DynamoDBClient): Promise<Ticket[]> {
	const dynamo = client || new DynamoDBClient({ region: REGION });
	try {
		const res = await dynamo.send(new ScanCommand({ TableName: TABLE_NAME }));
		if (!res.Items || res.Items.length === 0) {
			return [];
		}

		const tickets: Ticket[] = res.Items.map((item) => {
			return {
				id: item.ticketId?.S || '',
				userId: item.userId?.S || item.userEmail?.S || '',
				email: item.userEmail?.S || '',
				name: item.userName?.S || (item.userEmail?.S ? item.userEmail.S.split('@')[0] : 'Learner'),
				category: item.category?.S || 'bug',
				subject: item.title?.S || 'Issue Report',
				description: item.description?.S || '',
				deviceOs: item.deviceOs?.S || 'iOS',
				deviceVersion: item.deviceVersion?.S || '',
				appVersion: item.appVersion?.S || 'v2.0',
				status: (item.status?.S as any) || 'Pending',
				createdAt: item.createdAt?.S || new Date().toISOString(),
				updatedAt: item.updatedAt?.S,
				reply: item.adminReply?.S,
				resolvedAt: item.resolvedAt?.S,
				resolvedBy: item.resolvedBy?.S,
				org: item.org?.S || '7D Imaging',
				attachment: item.attachment?.S,
			};
		});

		// Sort newest first
		tickets.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
		return tickets;
	} catch (e) {
		console.error('[TicketService] Error fetching tickets from DynamoDB:', e);
		return [];
	}
}

/**
 * Resolve ticket in DynamoDB table
 */
export async function resolveTicketInDynamo(
	client: DynamoDBClient | undefined,
	id: string,
	reply: string,
	resolvedBy: string = 'Admin'
): Promise<boolean> {
	const dynamo = client || new DynamoDBClient({ region: REGION });
	const now = new Date().toISOString();
	try {
		await dynamo.send(
			new UpdateItemCommand({
				TableName: TABLE_NAME,
				Key: { ticketId: { S: id } },
				UpdateExpression: 'SET #st = :st, adminReply = :r, resolvedAt = :ra, resolvedBy = :rb, updatedAt = :ua',
				ExpressionAttributeNames: { '#st': 'status' },
				ExpressionAttributeValues: {
					':st': { S: 'Resolved' },
					':r': { S: reply },
					':ra': { S: now },
					':rb': { S: resolvedBy },
					':ua': { S: now },
				},
			})
		);
		return true;
	} catch (e) {
		console.warn('[TicketService] UpdateItem failed, falling back to GetItem + PutItem:', e);
		try {
			const existing = await dynamo.send(
				new GetItemCommand({
					TableName: TABLE_NAME,
					Key: { ticketId: { S: id } },
				})
			);
			const item = existing.Item || { ticketId: { S: id } };
			item.status = { S: 'Resolved' };
			item.adminReply = { S: reply };
			item.resolvedAt = { S: now };
			item.resolvedBy = { S: resolvedBy };
			item.updatedAt = { S: now };
			await dynamo.send(
				new PutItemCommand({
					TableName: TABLE_NAME,
					Item: item,
				})
			);
			return true;
		} catch (fallbackErr) {
			console.error('[TicketService] Error resolving ticket in DynamoDB (both UpdateItem and PutItem failed):', fallbackErr);
			return false;
		}
	}
}

/**
 * Delete ticket from DynamoDB table
 */
export async function deleteTicketFromDynamo(
	client: DynamoDBClient | undefined,
	id: string
): Promise<boolean> {
	const dynamo = client || new DynamoDBClient({ region: REGION });
	try {
		await dynamo.send(
			new DeleteItemCommand({
				TableName: TABLE_NAME,
				Key: { ticketId: { S: id } },
			})
		);
		return true;
	} catch (e) {
		console.error('[TicketService] Error deleting ticket from DynamoDB:', e);
		return false;
	}
}

// Fallback synchronous stubs
export function getTickets(): Ticket[] {
	return [];
}

export function saveTickets(_tickets: Ticket[]) {
	// No-op (persisted to DynamoDB)
}

export function deleteTicket(_id: string): Ticket[] {
	return [];
}

export function createTicket(
	userId: string,
	email: string,
	name: string,
	subject: string,
	description: string,
	attachment?: string,
	org?: string
): Ticket {
	const newTicket: Ticket = {
		id: "TICK-" + Date.now().toString().slice(-6),
		userId,
		email,
		name,
		category: "Web Feedback",
		subject,
		description,
		attachment,
		org: org || "Global",
		status: "Pending",
		createdAt: new Date().toISOString(),
		updatedAt: new Date().toISOString(),
	};

	try {
		const dynamo = new DynamoDBClient({ region: REGION });
		const item: Record<string, any> = {
			ticketId: { S: newTicket.id },
			userId: { S: newTicket.userId },
			userEmail: { S: newTicket.email },
			userName: { S: newTicket.name },
			category: { S: newTicket.category },
			title: { S: newTicket.subject },
			description: { S: newTicket.description },
			org: { S: newTicket.org || "Global" },
			status: { S: "Pending" },
			deviceOs: { S: "Web Portal" },
			createdAt: { S: newTicket.createdAt },
			updatedAt: { S: newTicket.updatedAt },
		};
		if (newTicket.attachment) {
			item.attachment = { S: newTicket.attachment };
		}
		dynamo.send(new PutItemCommand({
			TableName: TABLE_NAME,
			Item: item,
		})).catch((err: any) => console.warn("[ticketService] Background ticket save notice:", err));
	} catch (e) {
		console.warn("[ticketService] Error initializing dynamo put:", e);
	}

	return newTicket;
}
